import DSBox from '../../../../design-system/DSBox';
import KlydoGrid from './KlydoGrid';
import useKlydosList from '../hooks/useKlydosList';
import DSPrimaryButton from '../../../../design-system/DSPrimaryButton';
import DSTabGradientViewPaper from '../../../../design-system/Paper/DSTabGradientViewPaper';
import { useTheme } from '@mui/material';
import useDeviceType from '../../../../hooks/useDeviceType';
import { filterTabs, KlydoFilterTab } from '../types/filterTypes';
import DSTabButton from '../../../../design-system/DSTabButton';
import { ReactComponent as PlusIcon } from '../../../../design-system/Icons/PlusIcon.svg';
import { DSTypography } from '../../../../design-system/Typography';
import loading from '../../../../images/loading.png';

const Klydos = () => {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    handleCreateClick,
    activeFilter,
    handleFilterChange,
  } = useKlydosList();

  const { spacing } = useTheme();
  const { isMobile } = useDeviceType();

  const getEmptyStateMessage = () => {
    if (activeFilter === KlydoFilterTab.All) {
      return 'Click to create a new Klydo';
    }
    return `There are no ${activeFilter} klydos`;
  };

  const RenderContent = () => {
    if (isLoading) {
      return (
        <DSBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="70vh"
        >
          <img
            src={loading}
            alt="Logo"
            width={spacing(26)}
            height={spacing(27)}
          />
          <DSTypography variant="body1" fontSize={spacing(2.5)}>
            Loading...
          </DSTypography>
        </DSBox>
      );
    }

    if (!data?.length) {
      return (
        <DSBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="70vh"
        >
          <img
            src={loading}
            alt="Logo"
            width={spacing(26)}
            height={spacing(27)}
          />
          <DSTypography
            onClick={
              activeFilter === KlydoFilterTab.All
                ? handleCreateClick
                : undefined
            }
            variant="body1"
            fontSize={spacing(2.5)}
            sx={{
              textDecorationLine:
                activeFilter === KlydoFilterTab.All ? 'underline' : 'none',
              cursor:
                activeFilter === KlydoFilterTab.All ? 'pointer' : 'default',
            }}
          >
            {getEmptyStateMessage()}
          </DSTypography>
        </DSBox>
      );
    }

    return <KlydoGrid klydos={data} />;
  };

  return (
    <DSTabGradientViewPaper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '87vh',
        paddingX: 2,
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingY: spacing(3),
          flexWrap: 'wrap',
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            gap: spacing(1),
            flexDirection: 'row',
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: {
              xxs: '100%',
              xs: '50%',
              sm: '60%',
              md: '70%',
              lg: '80%',
            },
            paddingBottom: spacing(0.5),
            '&::-webkit-scrollbar': {
              height: '4px',
            },
          }}
        >
          {filterTabs.map((tab) => (
            <DSTabButton
              key={tab}
              title={tab}
              isSelected={activeFilter === tab}
              onClick={() => handleFilterChange(tab)}
            />
          ))}
        </DSBox>
        {!isMobile && (
          <DSPrimaryButton
            endIcon={<PlusIcon />}
            onClick={() => handleCreateClick()}
          >
            Create
          </DSPrimaryButton>
        )}
      </DSBox>
      <DSBox
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          overflowY: 'scroll',
          overflowX: 'hidden',
          maxHeight: '80vh',
          // `paddingX` would resolve after a `paddingRight` key and silently zero
          // it, so the gutter is expressed once, on the axis that needs it.
          paddingLeft: 0,
          paddingRight: { xs: 0, sm: spacing(2) },
        }}
      >
        <RenderContent />
      </DSBox>
    </DSTabGradientViewPaper>
  );
};
export default Klydos;
